import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import {
  app,
  auth,
  db,
  rtdb,
  storage,
  functions,
  remoteConfig,
  performance,
  messaging,
  analytics,
  trackEvent,
  getRemoteConfigValue,
  getRemoteConfigBool,
  requestFCMPermission,
  onFCMMessage,
  onAuthStateChanged,
  fetchAndActivate,
  getValue,
} from "@/lib/firebase";
import type {
  Auth,
  Firestore,
  Database,
  FirebaseStorage,
  Functions,
  RemoteConfig,
  FirebasePerformance,
  Messaging,
  Analytics,
  User,
} from "@/lib/firebase";

// ─── FCM VAPID key (get from Firebase Console → Cloud Messaging → Web Push) ──
const VAPID_KEY = ""; // ← paste your VAPID key here

// ─── Context Type ─────────────────────────────────────────────────────────────
interface FirebaseContextType {
  // Core
  app: typeof app;

  // Services
  auth: Auth;
  db: Firestore;
  rtdb: Database;
  storage: FirebaseStorage;
  functions: Functions;
  remoteConfig: RemoteConfig;
  performance: FirebasePerformance | null;
  messaging: Messaging | null;
  analytics: Analytics | null;

  // Auth state
  firebaseUser: User | null;
  firebaseLoading: boolean;

  // Remote Config
  remoteConfigReady: boolean;
  getConfigValue: (key: string) => Promise<string>;
  getConfigBool: (key: string) => Promise<boolean>;

  // FCM
  fcmToken: string | null;
  requestPushPermission: () => Promise<string | null>;

  // Analytics
  trackEvent: (name: string, params?: Record<string, any>) => void;

  // App state
  maintenanceMode: boolean;
  featureFlags: Record<string, boolean>;
}

const FirebaseContext = createContext<FirebaseContextType | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────
export function FirebaseProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [firebaseLoading, setFirebaseLoading] = useState(true);
  const [remoteConfigReady, setRemoteConfigReady] = useState(false);
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({
    live_streams: true,
    marketplace: true,
    ai_assist: true,
  });

  // ── Firebase Auth state ──────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
      setFirebaseLoading(false);
      if (user) {
        trackEvent("firebase_user_active", { uid: user.uid });
      }
    });
    return unsubscribe;
  }, []);

  // ── Remote Config bootstrap ───────────────────────────────────────────────
  useEffect(() => {
    const bootstrap = async () => {
      try {
        await fetchAndActivate(remoteConfig);
        const maintenance = getValue(remoteConfig, "maintenance_mode").asBoolean();
        const liveStreams = getValue(remoteConfig, "feature_live_streams").asBoolean();
        const marketplace = getValue(remoteConfig, "feature_marketplace").asBoolean();
        const aiAssist = getValue(remoteConfig, "feature_ai_assist").asBoolean();

        setMaintenanceMode(maintenance);
        setFeatureFlags({
          live_streams: liveStreams,
          marketplace: marketplace,
          ai_assist: aiAssist,
        });
        setRemoteConfigReady(true);
      } catch (e) {
        console.warn("[Firebase] Remote Config fetch failed:", e);
        setRemoteConfigReady(true); // still mark ready so app doesn't hang
      }
    };
    bootstrap();
  }, []);

  // ── FCM foreground messages ───────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onFCMMessage((payload) => {
      console.log("[FCM] Foreground message:", payload);
      // You can show a toast/notification here
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // ── Request push permission & token ───────────────────────────────────────
  const requestPushPermission = async (): Promise<string | null> => {
    if (!VAPID_KEY) {
      console.warn("[FCM] VAPID key not configured. Add it to FirebaseContext.tsx");
      return null;
    }
    const token = await requestFCMPermission(VAPID_KEY);
    if (token) setFcmToken(token);
    return token;
  };

  const value: FirebaseContextType = {
    app,
    auth,
    db,
    rtdb,
    storage,
    functions,
    remoteConfig,
    performance,
    messaging,
    analytics,
    firebaseUser,
    firebaseLoading,
    remoteConfigReady,
    getConfigValue: getRemoteConfigValue,
    getConfigBool: getRemoteConfigBool,
    fcmToken,
    requestPushPermission,
    trackEvent,
    maintenanceMode,
    featureFlags,
  };

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useFirebase() {
  const ctx = useContext(FirebaseContext);
  if (!ctx) throw new Error("useFirebase must be used inside <FirebaseProvider>");
  return ctx;
}

// ─── Focused sub-hooks ────────────────────────────────────────────────────────
export const useFirebaseAuth = () => {
  const { auth, firebaseUser, firebaseLoading } = useFirebase();
  return { auth, firebaseUser, firebaseLoading };
};

export const useFirestore = () => {
  const { db } = useFirebase();
  return db;
};

export const useRealtimeDB = () => {
  const { rtdb } = useFirebase();
  return rtdb;
};

export const useFirebaseStorage = () => {
  const { storage } = useFirebase();
  return storage;
};

export const useFirebaseFunctions = () => {
  const { functions } = useFirebase();
  return functions;
};

export const useRemoteConfig = () => {
  const { remoteConfig, remoteConfigReady, getConfigValue, getConfigBool, featureFlags, maintenanceMode } = useFirebase();
  return { remoteConfig, remoteConfigReady, getConfigValue, getConfigBool, featureFlags, maintenanceMode };
};

export const useFirebaseAnalytics = () => {
  const { analytics, trackEvent } = useFirebase();
  return { analytics, trackEvent };
};

export const useFCM = () => {
  const { messaging, fcmToken, requestPushPermission } = useFirebase();
  return { messaging, fcmToken, requestPushPermission };
};

export default FirebaseContext;
